import {
  Allergy,
  ChatReport,
  Condition,
  Configuration,
  Consumer,
  Country,
  EstimatedVisitCost,
  HealthPlan,
  Medication,
  PaymentMethod,
  Pharmacy,
  Practice,
  PracticeList,
  Provider,
  ProviderDetails,
  RelationshipToSubscriberCode,
  State,
  Subscription,
  Transfer,
  VisitContext,
  Visit,
  VisitSummary,
  Vitals,
  VideoConsole,
  OnDemandSpecialty,
  Appointment,
  HealthDocumentRecord,
  SystemConfiguration,
  TestMyComputerConfiguration
} from 'lib/amwell/sdk/types';
import { PharmacyType } from 'lib/amwell/sdk/enums';
import { SpeedPass } from 'store/amwell/types';
import {
  RegisterDependentForm,
  UpdateInsuranceSubscriptionForm,
  UpdatePaymentMethodForm
} from './consumerService';
import { UpdateVisitContextForm, WebRTCContextElements } from './visitService';
import { Storage } from './storageService';

export * from './storageService';

// Export types of client services here
export * from './consumerService';
export * from './visitService';

export type SetData<T> = { [P in keyof T]: T[P] };
export type HydrateData<T> = SetData<T>;
export type RemoveData = { keys: (keyof Storage)[] };
export type ClearData = { opts: 'all' | 'session' };

// Initialization Data Params
export interface InitData {
  configuration: Configuration;
}

// Test My Computer Data Params
export interface GetTestWizardData {
  configuration: TestMyComputerConfiguration;
}

// Authentication Data Params
export interface LoginWithTokenData {
  visitToken: string;
}
export interface LoginData {
  email: string;
  password: string;
}
export interface RequestPasswordResetData {
  email: string;
  lastName: string;
  dob: Date;
}

// Consumer
export interface UpdateConsumerLegalResidenceData {
  stateCode: string;
  countryCode?: string;
}
export interface SetConsumerData {
  consumer: Consumer;
}

// Consumer (Parent) Dependents Data Params
export interface AddDependentData {
  registerDependentForm: RegisterDependentForm;
}

// Active Consumer Medical History Data Params
export interface UpdateMedicationsData {
  medications: Medication[];
}
export interface SearchMedicationsData {
  searchText: string;
}
export interface UpdateConditionsData {
  conditions: Condition[];
}
export interface UpdateAllergiesData {
  allergies: Allergy[];
}
export interface UpdateVitalsData {
  vitals: Vitals;
}
export interface AddHealthDocumentData {
  file: Blob | File;
  fileName: string;
  visit?: Visit;
  comment?: string;
}
export interface RemoveHealthDocumentData {
  healthDocument: HealthDocumentRecord;
}

// Active Consumer Insurance Data Params
export interface UpdateInsuranceSubscriptionData {
  updateInsuranceSubscriptionForm: UpdateInsuranceSubscriptionForm;
}

// Active Consumer Payment Data Params
export interface UpdatePaymentMethodData {
  updatePaymentMethodForm: UpdatePaymentMethodForm;
}

// Active Consumer Finding Visit Data Params
export interface UpdatePracticeData {
  practice: Practice;
}

export interface AddServiceKeyData {
  key: string;
}

export interface ProvidersData {
  practice: Practice;
}

export interface ProviderDetailData {
  provider: Provider;
}

export interface HasFirstAvailableData {
  practice: Practice;
}

export interface GetFirstAvailableData {
  practice: Practice;
}

export interface EstimatedVisitCostData {
  providerDetails: ProviderDetails;
}

// Active Consumer Pharmacies
export interface SearchPharmaciesData {
  city: string;
  state: State;
  zipCode: string;
  pharmacyType: PharmacyType;
}

export interface UpdatePreferredPharmacyData {
  pharmacy: Pharmacy;
}

// Active Consumer Appointment
export interface GetAppointmentsData {
  consumer: Consumer;
}
export interface GetAppointmentData {
  appointment: Appointment;
}

// Visit Context Data Params
export interface CreateVisitContextWithAppointmentData {
  appointment: Appointment;
}
export interface UpdateVisitContextData {
  updateVisitContextForm: UpdateVisitContextForm;
}

// Visit Data Params
export interface UpdateCouponCodeData {
  couponCode: string;
}

export interface VisitData {
  visit: Visit;
}

export interface StartWebRTCVisitData {
  visit: Visit;
  contextElements: WebRTCContextElements;
}

export interface SendVisitSummaryData {
  hipaaNoticeAccepted: boolean;
  emailAddresses?: string[];
  faxNumbers?: string[];
}

export interface SetupWaitingRoomAlertsData {
  phoneNumber: string;
  providerReadyAlert?: boolean;
  movedUpInQueueAlert?: boolean;
}

export interface SubmitVisitFeedbackData {
  feedbackQuestion: string;
  feedbackAnswer: string;
  engagementRating: number;
  providerRating: number;
}

export interface AddChatMessageData {
  message: string;
  lastPolledChatOrdinal: number;
}

export interface DeclineSuggestedTransferData {
  dontAskAgain: boolean;
}

export interface VisitContextAndDisclaimersAcknowledged {
  visitContext: VisitContext;
  disclaimersAcknowledged: boolean;
}

export interface AppointmentVisitContextRequirements {
  visitContext: VisitContext;
  disclaimersAcknowledged: boolean;
  activeConsumer: Consumer;
  provider: Provider;
}

export interface VisitTransfer extends Omit<Transfer, 'visit'> {
  disclaimersAcknowledged: boolean;
  provider: ProviderDetails;
  visit: Visit | null;
}

export interface GetAllPharmaciesPayloadData {
  latitude: number;
  longitude: number;
  pharmacyType: PharmacyType;
  radius: number;
}

export interface GetSpeedPassData {
  provider: Provider;
}

export interface CreateVisitFromSpeedPassData {
  speedPass: SpeedPass;
}

export interface Dispatch {
  // Instance
  hydrate: (data: HydrateData<Storage>) => Storage;
  set: (data: SetData<Storage>) => Promise<void>;
  get: () => Storage;
  remove: (data: RemoveData) => Promise<void>;
  clear: (data: ClearData) => Promise<void> | void;

  // //////// //
  // Dispatch //
  // //////// //

  // Amwell System
  getHealthPlans: () => Promise<HealthPlan[]>;
  getCountries: () => Promise<Country[]>;
  getRelationshipsToSubscriber: () => Promise<RelationshipToSubscriberCode[]>;
  getSystemConfiguration: () => SystemConfiguration;

  // Initialization
  init: (data: InitData) => Promise<boolean>;

  // Authentication
  loginWithToken: (data: LoginWithTokenData) => Promise<Consumer>;
  login: (data: LoginData) => Promise<Consumer>;
  logout: () => Promise<boolean>;
  requestPasswordReset: (data: RequestPasswordResetData) => Promise<boolean>;

  // Consumer
  updateConsumerLegalResidence: (data: UpdateConsumerLegalResidenceData) => Promise<Consumer>;
  setConsumer: (data: SetConsumerData) => Promise<Consumer>;

  // Consumer (Parent) Dependents
  getDependents: () => Promise<Consumer[]>;
  addDependent: (data: AddDependentData) => Promise<Consumer[]>;

  // Active Consumer Medical History
  getMedications: () => Promise<Medication[]>;
  updateMedications: (data: UpdateMedicationsData) => Promise<Medication[]>;

  getConditions: () => Promise<Condition[]>;
  updateConditions: (data: UpdateConditionsData) => Promise<Condition[]>;

  getAllergies: () => Promise<Allergy[]>;
  updateAllergies: (data: UpdateAllergiesData) => Promise<Allergy[]>;

  getVitals: () => Promise<Vitals>;
  updateVitals: (data: UpdateVitalsData) => Promise<Vitals>;

  searchHealthDocuments: () => Promise<HealthDocumentRecord[]>;
  addHealthDocument: (data: AddHealthDocumentData) => Promise<HealthDocumentRecord[]>;
  removeHealthDocument: (data: RemoveHealthDocumentData) => Promise<HealthDocumentRecord[]>;

  // Active Consumer Insurance
  getInsuranceSubscription: () => Promise<Subscription>;
  updateInsuranceSubscription: (data: UpdateInsuranceSubscriptionData) => Promise<Consumer>;
  removeInsuranceSubscription: () => Promise<Consumer>;

  // Active Consumer Payment
  getPaymentMethod: () => Promise<PaymentMethod | null>;
  updatePaymentMethod: (data: UpdatePaymentMethodData) => Promise<PaymentMethod>;

  // Active Consumer Finding Visit
  practices: () => Promise<PracticeList>;
  updatePractice: (data: UpdatePracticeData) => Promise<Practice>;

  addServiceKey: (data: AddServiceKeyData) => Promise<Consumer>;

  hasFirstAvailable: (data: HasFirstAvailableData) => Promise<boolean>;
  getFirstAvailable: (data: GetFirstAvailableData) => Promise<OnDemandSpecialty | null>;

  providers: (data: ProvidersData) => Promise<Provider[]>;
  providerDetail: (data: ProviderDetailData) => Promise<ProviderDetails>;

  estimatedVisitCost: (data: EstimatedVisitCostData) => Promise<EstimatedVisitCost>;

  // Active Consumer Pharmacy
  searchPharmacies: (data: SearchPharmaciesData) => Promise<Pharmacy[]>;
  updatePreferredPharmacy: (data: UpdatePreferredPharmacyData) => Promise<boolean>;
  getPreferredPharmacy: () => Promise<Pharmacy>;

  // Active Consumer Appointment
  getAppointments: (data: GetAppointmentsData) => Promise<Appointment[]>;
  getAppointment: (data: GetAppointmentData) => Promise<Appointment>;

  // Visit Context
  getVisitContext: () => Promise<VisitContextAndDisclaimersAcknowledged>;
  createVisitContextWithAppointment: (
    data: CreateVisitContextWithAppointmentData
  ) => Promise<AppointmentVisitContextRequirements>;
  updateVisitContext: (
    data: UpdateVisitContextData
  ) => Promise<VisitContextAndDisclaimersAcknowledged>;

  // Visit
  createVisit: () => Promise<Visit>;
  updateCouponCode: (data: UpdateCouponCodeData) => Promise<Visit>;
  startVisit: (data: VisitData) => Promise<void>;
  acceptSuggestedTransfer: (data: VisitData) => Promise<Visit>;
  declineSuggestedTransfer: (data: DeclineSuggestedTransferData) => Promise<void>;
  handleTransfer: (data: VisitData) => Promise<VisitTransfer>;
  startWebRTCVisit: (data: StartWebRTCVisitData) => Promise<VideoConsole>;
  cancelVisit: (data: VisitData) => Promise<void>;
  endVisit: (data: VisitData) => Promise<void>;
  getVisitSummary: () => Promise<VisitSummary>;
  sendVisitSummary: (data: SendVisitSummaryData) => Promise<VisitSummary>;
  setupWaitingRoomAlerts: (data: SetupWaitingRoomAlertsData) => Promise<boolean>;
  submitVisitFeedback: (data: SubmitVisitFeedbackData) => Promise<boolean>;
  addChatMessage: (data: AddChatMessageData) => Promise<ChatReport>;
}

export type DataParams<T> = T extends undefined ? any : T;
export type ExecuteData<T extends keyof Dispatch> = DataParams<Parameters<Dispatch[T]>[0]>;
export type ExecuteReturn<T extends keyof Dispatch> = ReturnType<Dispatch[T]>;
